import React, {useContext, useEffect, useState, useCallback, useMemo} from "react";
import BedRockContext from "../../BedRockContext";
import {PageTitle} from "../../../_metronic/layout/core";
import {GenericTable} from "../generic/table/GenericTable";
import {defaultList} from "../generic/InitialStates";
import {ExpenseCreateDialog} from "./ExpenseCreateDialog";
import {HeaderColumnFormatter} from "../../column-formatters/HeaderColumnFormatter";
import {DMSDateColumnFormatter} from "../../column-formatters/DMSDateColumnFormatter";
import {withSyncDot} from "../../column-formatters/SyncStatusColumnFormatter";
import {ENTITY_ID_CLIENT_EXPENSE, ENTITY_ID_EXPENSE_CATEGORY} from "../generic/Constants";
import {SyncProgressDialog} from "../../generic-components/SyncProgressDialog";
import {useSyncOperations} from "../../hooks/useSyncOperations";
import {useSyncEnabled} from "../../hooks/useSyncEnabled";
import {syncPull} from "../generic/crud";
import {showToast, TOAST_SUCCESS, TOAST_FAILURE} from "../../utils/toast/Toast";
import {formatDateTime} from "../../utils/Utils";

export function ExpensesPage() {
    const myData = useContext(BedRockContext);
    const [reload, setReload] = useState(1);
    const [pageLoadComplete, setPageLoadComplete] = useState(false);
    const [showModal, setShowModal] = useState(false);

    const {syncEnabled, pushEnabled, pullEnabled} = useSyncEnabled(ENTITY_ID_CLIENT_EXPENSE);

    const reloadData = useCallback(() => {
        setReload(prev => prev + 1);
    }, []);

    const {
        showDialog: showSyncDialog,
        syncType,
        isInProgress,
        syncResult,
        pushToServer,
        closeDialog: closeSyncDialog
    } = useSyncOperations(ENTITY_ID_CLIENT_EXPENSE, reloadData, {pushEnabled, pullEnabled});

    const profileBreadCrumbs = [
        {title: "Home", path: "/dashboard", isSeparator: false, isActive: false},
        {title: "", path: "", isSeparator: true, isActive: false},
    ];

    const syncCategories = useCallback(async () => {
        try {
            const response = await syncPull(ENTITY_ID_EXPENSE_CATEGORY);
            const result = response.data;
            if (result.success) {
                showToast("Categories synced from server!", TOAST_SUCCESS);
            } else {
                showToast(`Category sync failed: ${result.message}`, TOAST_FAILURE);
            }
        } catch (error) {
            const errorMsg = error.response?.data?.message || error.message || 'Unknown error';
            showToast(`Category sync failed: ${errorMsg}`, TOAST_FAILURE);
        }
    }, []);

    const columns = useMemo(() => {
        const baseColumns = [
            {
                dataField: "categoryName",
                text: "Category",
                search: true,
                formatter: syncEnabled ? withSyncDot(HeaderColumnFormatter) : HeaderColumnFormatter
            },
            {
                dataField: "totalAmount",
                text: "Amount",
                formatter: (cell) => cell != null ? `\u20B9 ${Number(cell).toLocaleString('en-IN')}` : '-',
            },
            {
                dataField: "ledgerAccountName",
                text: "Paid From",
            },
            {
                dataField: "narration",
                text: "Narration",
            },
            {
                dataField: "expenseDate",
                text: "Expense Date",
                formatter: (cell) => formatDateTime(cell),
            },
        ];

        return baseColumns;
    }, [syncEnabled]);

    useEffect(() => {
        if (myData && myData.setBaseData) {
            const toolBarButtons = [];

            toolBarButtons.push({
                name: "Sync Categories",
                color: "warning",
                icon: "fa fa-sync",
                onClick: syncCategories,
            });

            if (syncEnabled && pushEnabled) {
                toolBarButtons.push({
                    name: "Push to Server",
                    color: "info",
                    icon: "fa fa-cloud-upload-alt",
                    onClick: pushToServer,
                });
            }

            toolBarButtons.push({
                name: "ADD EXPENSE",
                color: "primary",
                icon: "fa fa-plus",
                onClick: () => setShowModal(true),
            });

            myData.setBaseData({toolBarButtons});
        }

        setPageLoadComplete(true);
    }, [pushToServer, syncEnabled, pushEnabled, syncCategories]);

    function handleClose() {
        setShowModal(false);
        reloadData();
    }

    const BaseCard = () => (
        <div className="card card-body">
            {/* Sync Info Banner */}
            {syncEnabled && (
                <div className="alert alert-info d-flex align-items-center mb-4" role="alert">
                    <i className="fas fa-info-circle me-3 fs-3"></i>
                    <div>
                        <strong>Data Retention Policy</strong>
                        <br />
                        <small>
                            Synced records (marked with <span style={{display:'inline-block',width:8,height:8,borderRadius:'50%',backgroundColor:'#50cd89',verticalAlign:'middle'}}></span>) will be automatically
                            removed from this device after <strong>2 days</strong>. These records are safely backed up on the server
                            and can be accessed from the main system.
                        </small>
                    </div>
                </div>
            )}

            {pageLoadComplete && (
                <GenericTable
                    reload={reload}
                    defaultData={defaultList}
                    columns={columns}
                    entityId={ENTITY_ID_CLIENT_EXPENSE}
                />
            )}
        </div>
    );

    return (
        <>
            <PageTitle breadcrumbs={profileBreadCrumbs}>Expenses</PageTitle>
            <BaseCard/>
            <ExpenseCreateDialog show={showModal} handleClose={handleClose}/>
            {syncEnabled && (
                <SyncProgressDialog
                    show={showSyncDialog}
                    onClose={closeSyncDialog}
                    syncType={syncType}
                    isInProgress={isInProgress}
                    syncResult={syncResult}
                />
            )}
        </>
    );
}
